import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import { NavigationHeader } from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { customerService, Customer, CustomerInsert } from '../../services/customerService';

const CustomerProfileEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('id');
  const viewOnly = searchParams.get('view') === 'true';

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CustomerInsert>({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    notes: '',
  });

  useEffect(() => {
    if (customerId) {
      loadCustomer();
    }
  }, [customerId]);

  const loadCustomer = async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      setError(null);
      const customer = await customerService.getById(customerId);
      if (customer) {
        setFormData({
          name: customer.name || '',
          email: customer.email || '',
          phone: customer.phone || '',
          company: customer.company || '',
          address: customer.address || '',
          city: customer.city || '',
          postal_code: customer.postal_code || '',
          country: customer.country || '',
          notes: customer.notes || '',
        });
      } else {
        alert('Customer not found');
        navigate('/customer-management-dashboard');
      }
    } catch (err) {
      console.error('Error loading customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CustomerInsert, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    if (!formData.name?.trim()) {
      alert('Customer name is required');
      return false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      alert('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      if (customerId) {
        await customerService.update(customerId, formData);
        alert('Customer updated successfully!');
      } else {
        await customerService.create(formData);
        alert('Customer created successfully!');
      }
      navigate('/customer-management-dashboard');
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading customer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <NavigationHeader />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  {viewOnly ? 'View Customer' : customerId ? 'Edit Customer' : 'Add New Customer'}
                </h1>
                <p className="text-sm text-text-secondary mt-1">
                  {viewOnly
                    ? 'View customer details' :'Enter customer information and contact details'}
                </p>
              </div>
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/customer-management-dashboard')}
              >
                Back to List
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Icon name="User" size={20} className="text-primary" />
                  Customer Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Customer Name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Enter full name"
                    required
                    disabled={viewOnly}
                  />
                  <Input
                    label="Company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="Enter company name"
                    disabled={viewOnly}
                  />
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Icon name="Mail" size={20} className="text-primary" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@example.com"
                    disabled={viewOnly}
                  />
                  <Input
                    label="Phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    disabled={viewOnly}
                  />
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Icon name="MapPin" size={20} className="text-primary" />
                  Address
                </h2>
                <div className="space-y-4">
                  <Input
                    label="Street Address"
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Enter street address"
                    disabled={viewOnly}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="City"
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Enter city"
                      disabled={viewOnly}
                    />
                    <Input
                      label="Postal Code"
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => handleChange('postal_code', e.target.value)}
                      placeholder="Enter postal code"
                      disabled={viewOnly}
                    />
                    <Input
                      label="Country"
                      type="text"
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      placeholder="Enter country"
                      disabled={viewOnly}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Icon name="FileText" size={20} className="text-primary" />
                  Additional Notes
                </h2>
                <textarea
                  className="w-full min-h-[100px] px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-disabled focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Add any additional notes about this customer..."
                  disabled={viewOnly}
                />
              </div>

              {!viewOnly && (
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/customer-management-dashboard')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    iconName="Save"
                    iconPosition="left"
                    onClick={handleSave}
                    loading={saving}
                  >
                    {customerId ? 'Update Customer' : 'Create Customer'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomerProfileEditor;